const MINIMUM_SPACER_DISTANCE = 0.001;
const MINIMUM_PARALLEL_DOT_PRODUCT = 0.995;
const MINIMUM_LINE_OVERLAP_RATIO = 0.25;

// The area sign tells which side of every ordered contour edge points outside the shape.
const getPolygonSignedArea = (points) => points.reduce((area, point, pointIndex) => {
  const nextPoint = points[(pointIndex + 1) % points.length];
  return area + point.x * nextPoint.y - nextPoint.x * point.y;
}, 0) / 2;

// Returns the unit normal that points away from the source shape for the selected contour edge.
const getOutwardNormal = (baseFrom, baseTo, shapePoints) => {
  const edgeX = baseTo.x - baseFrom.x;
  const edgeY = baseTo.y - baseFrom.y;
  const edgeLength = Math.hypot(edgeX, edgeY);
  const shapeArea = getPolygonSignedArea(shapePoints);
  if (edgeLength <= MINIMUM_SPACER_DISTANCE || Math.abs(shapeArea) <= MINIMUM_SPACER_DISTANCE) {
    return null;
  }

  const unitX = edgeX / edgeLength;
  const unitY = edgeY / edgeLength;
  return shapeArea > 0
    ? {x: unitY, y: -unitX}
    : {x: -unitY, y: unitX};
};

// Measures how much of a target line overlaps the selected edge along the edge direction.
const getLongitudinalOverlap = (baseFrom, baseLength, unitX, unitY, targetLine) => {
  const targetFromProjection = (
    (targetLine.from.x - baseFrom.x) * unitX
    + (targetLine.from.y - baseFrom.y) * unitY
  );
  const targetToProjection = (
    (targetLine.to.x - baseFrom.x) * unitX
    + (targetLine.to.y - baseFrom.y) * unitY
  );
  const targetStart = Math.min(targetFromProjection, targetToProjection);
  const targetEnd = Math.max(targetFromProjection, targetToProjection);
  return Math.max(0, Math.min(baseLength, targetEnd) - Math.max(0, targetStart));
};

// Chooses the closest eligible parallel line inside the screen-space snapping radius.
const getSnappedDistance = ({
  baseFrom,
  baseTo,
  outwardNormal,
  requestedDistance,
  targetLines,
  snapOffset,
}) => {
  const baseX = baseTo.x - baseFrom.x;
  const baseY = baseTo.y - baseFrom.y;
  const baseLength = Math.hypot(baseX, baseY);
  const unitX = baseX / baseLength;
  const unitY = baseY / baseLength;
  const baseCenter = {
    x: (baseFrom.x + baseTo.x) / 2,
    y: (baseFrom.y + baseTo.y) / 2,
  };
  let closestSnap = null;

  targetLines.forEach(targetLine => {
    const targetX = targetLine.to.x - targetLine.from.x;
    const targetY = targetLine.to.y - targetLine.from.y;
    const targetLength = Math.hypot(targetX, targetY);
    if (targetLength <= MINIMUM_SPACER_DISTANCE) {
      return;
    }

    const parallelDotProduct = Math.abs(
      unitX * (targetX / targetLength) + unitY * (targetY / targetLength)
    );
    if (parallelDotProduct < MINIMUM_PARALLEL_DOT_PRODUCT) {
      return;
    }

    const longitudinalOverlap = getLongitudinalOverlap(
      baseFrom,
      baseLength,
      unitX,
      unitY,
      targetLine
    );
    const minimumOverlap = Math.min(baseLength, targetLength) * MINIMUM_LINE_OVERLAP_RATIO;
    if (longitudinalOverlap < minimumOverlap) {
      return;
    }

    const targetCenter = {
      x: (targetLine.from.x + targetLine.to.x) / 2,
      y: (targetLine.from.y + targetLine.to.y) / 2,
    };
    const targetDistance = (
      (targetCenter.x - baseCenter.x) * outwardNormal.x
      + (targetCenter.y - baseCenter.y) * outwardNormal.y
    );
    const snapDifference = Math.abs(targetDistance - requestedDistance);
    if (targetDistance <= MINIMUM_SPACER_DISTANCE || snapDifference > snapOffset) {
      return;
    }

    if (!closestSnap || snapDifference < closestSnap.difference) {
      closestSnap = {distance: targetDistance, difference: snapDifference};
    }
  });

  return closestSnap;
};

/**
 * Builds the moving edge of a spacer in screen coordinates.
 * An inward mouse position returns an invalid zero-depth preview. An outward position may snap
 * to a parallel target line when the target has enough longitudinal overlap with the source edge.
 *
 * @return {{points: Array<{x: number, y: number}>, isValid: boolean, isSnapped: boolean}}
 */
export const getSpacerPreviewGeometry = ({
  baseFrom,
  baseTo,
  shapePoints,
  mousePosition,
  targetLines = [],
  snapOffset = 0,
}) => {
  const outwardNormal = getOutwardNormal(baseFrom, baseTo, shapePoints);
  if (!outwardNormal) {
    return {points: [baseTo, baseFrom], isValid: false, isSnapped: false};
  }

  const baseCenter = {
    x: (baseFrom.x + baseTo.x) / 2,
    y: (baseFrom.y + baseTo.y) / 2,
  };
  const requestedDistance = (
    (mousePosition.x - baseCenter.x) * outwardNormal.x
    + (mousePosition.y - baseCenter.y) * outwardNormal.y
  );
  if (requestedDistance <= MINIMUM_SPACER_DISTANCE) {
    return {points: [baseTo, baseFrom], isValid: false, isSnapped: false};
  }

  const snap = getSnappedDistance({
    baseFrom,
    baseTo,
    outwardNormal,
    requestedDistance,
    targetLines,
    snapOffset,
  });
  const distance = snap ? snap.distance : requestedDistance;
  const offset = {
    x: outwardNormal.x * distance,
    y: outwardNormal.y * distance,
  };

  return {
    points: [
      {x: baseTo.x + offset.x, y: baseTo.y + offset.y},
      {x: baseFrom.x + offset.x, y: baseFrom.y + offset.y},
    ],
    isValid: true,
    isSnapped: Boolean(snap),
  };
};
